import { module } from 'modujs';
import { $body, $html } from '../utils/dom'
import Swup from 'swup';
import SwupFormsPlugin from '@swup/forms-plugin';
import SwupFragmentPlugin from '@swup/fragment-plugin';
import SwupLSPlugin from '../utils/swup/SwupLSPlugin';
import SwupModularPlugin from '../utils/swup/SwupModularPlugin';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupRouteNamePlugin from '@swup/route-name-plugin';
import SwupScriptsPlugin from '@swup/scripts-plugin';
import { CAMERA_MODE } from '../utils/mixins/CamerasHandler';
import { awaitFrame, awaitTimeout } from '../utils/tickers';
import { CSS_CLASS, CUSTOM_EVENT, ENV } from '../config';

export default class extends module {
    constructor(m) {
        super(m);

        this.onVisitStart = this.onVisitStart.bind(this);
        this.beforeContentReplace = this.beforeContentReplace.bind(this);
        this.onContentReplace = this.onContentReplace.bind(this);

        this.rulesPaths = JSON.parse(this.getData('rules-paths')) || {};
    }

    init() {
        this.load = new Swup({
            containers: ['[data-load-container]'],
            animateHistoryBrowsing: true,
            ignoreVisit: (url, { el, event } = {}) => {
                // Disable SWUP if target is careers template
                if(url.startsWith(this.rulesPaths['careers'])) {
                    return true;
                }
                return el?.closest('[data-no-swup]')
            },
            // cache: false,
            plugins: this.#getSwupPlugins(),
        });

        this.load.hooks.on('visit:start', this.onVisitStart);
        this.load.hooks.before('content:replace', this.beforeContentReplace);
        this.load.hooks.on('content:replace', this.onContentReplace);
        this.load.hooks.on('visit:abort', this.onVisitAbort);
    }

    #getSwupPlugins() {
        const plugins = [
            new SwupLSPlugin({ modularInstance: this }),
            new SwupModularPlugin({ modularInstance: this }),
            new SwupFormsPlugin(),
            new SwupScriptsPlugin({ optin: true })
        ];

        this.#addSwupPreloadPlugin(plugins);
        this.#addSwupRouteNamePlugin(plugins);
        this.#addSwupFragmentPlugin(plugins);

        return plugins;
    }

    #addSwupPreloadPlugin(plugins) {
        if (ENV.FLAGS.includes('disable-preload')) {
            return;
        }

        plugins.push(new SwupPreloadPlugin({}));
    }

    #addSwupRouteNamePlugin(plugins) {
        plugins.push(new SwupRouteNamePlugin({
            routes: [
                { name: 'explore', path: `${this.rulesPaths['explore']}(.*)` },
                { name: 'project-preview', path: `${this.rulesPaths['project-preview']}(.*)` },
                { name: 'project-showcase', path: `${this.rulesPaths['project-showcase']}(.*)` },
                { name: 'contact', path: `${this.rulesPaths['contact']}(.*)` },
            ]
        }));
    }

    #addSwupFragmentPlugin(plugins) {
        plugins.push(new SwupFragmentPlugin({
            rules: [
                { // Project listing update rule
                    from: [
                        `${this.rulesPaths['projects-listing']}(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['projects-listing']}(.*)`
                    ],
                    containers: ['#listing','#toolbar','#search','#filters'],
                    name: 'update-listing'
                },
                { // Team listing update rule
                    from: [
                        `${this.rulesPaths['team-listing']}(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['team-listing']}(.*)`
                    ],
                    containers: ['#listing','#toolbar','#search','#filters'],
                    name: 'update-listing'
                },
                // Search listing update rules
                {
                    from: [
                        `${this.rulesPaths['search']}/news/:query(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['search']}/news/:query(.*)`
                    ],
                    containers: ['#listing','#toolbar','#search'],
                    name: 'update-listing'
                },
                {
                    from: [
                        `${this.rulesPaths['search']}/project/:query(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['search']}/project/:query(.*)`
                    ],
                    containers: ['#listing','#toolbar','#search'],
                    name: 'update-listing'
                },
                {
                    from: [
                        `${this.rulesPaths['search']}/team/:query(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['search']}/team/:query(.*)`
                    ],
                    containers: ['#listing','#toolbar','#search'],
                    name: 'update-listing'
                },
                {
                    from: [
                        `${this.rulesPaths['search']}/discipline/:query(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['search']}/discipline/:query(.*)`
                    ],
                    containers: ['#listing','#toolbar','#search'],
                    name: 'update-listing'
                },
                // Global open modal rules
                {
                    from: [
                        `${this.rulesPaths['projects-listing']}(.*)`,
                        `${this.rulesPaths['team-listing']}(.*)`,
                        `${this.rulesPaths['leadership-listing']}(.*)`,
                        `${this.rulesPaths['founders-listing']}(.*)`,
                        `${this.rulesPaths['discipline-detail']}(.*)`,
                        `${this.rulesPaths['collection-detail']}(.*)`,
                        `${this.rulesPaths['search']}(.*)`,
                    ],
                    to: [
                        `${this.rulesPaths['project-detail']}(.*)`,
                        `${this.rulesPaths['team-detail']}(.*)`,
                        `${this.rulesPaths['project-preview']}(.*)`,
                        `${this.rulesPaths['article-detail']}(.*)`,
                        `${this.rulesPaths['insights']}(.*)`,
                        `${this.rulesPaths['office-detail']}(.*)`,
                    ],
                    containers: ['#modal'],
                    name: 'open-modal'
                },
                { // Specific modal opening rules from `project-showcase` template, does not include `project-preview` to prevent issues w/ redirection system
                    from: [
                        `${this.rulesPaths['project-showcase']}(.*)`,
                    ],
                    to: [
                        `${this.rulesPaths['project-detail']}(.*)`,
                        `${this.rulesPaths['team-detail']}(.*)`,
                    ],
                    containers: ['#modal'],
                    name: 'open-modal'
                },
                { // Specific modal opening rules from `contact` template
                    from: [
                        `${this.rulesPaths['contact']}(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['office-detail']}(.*)`
                    ],
                    containers: ['#modal'],
                    name: 'open-modal'
                },
                { // Specific modal closing rules towards `contact` template
                    from: [
                        `${this.rulesPaths['office-detail']}(.*)`,
                        `${this.rulesPaths['team-detail']}(.*)`,
                        `${this.rulesPaths['project-detail']}(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['contact']}(.*)`
                    ],
                    containers: ['#modal', '#listing'],
                    name: 'close-modal'
                },
                { // Specific modal closing rules towards `project-showcase` template
                    from: [
                        `${this.rulesPaths['project-detail']}(.*)`,
                        // DO NOT INCLUDE `project-preview` here otherwise it'll conflict w/ redirection system. That's why this rule is separated
                    ],
                    to: [
                        `${this.rulesPaths['project-showcase']}(.*)`,
                    ],
                    containers: ['#modal', '#listing'],
                    name: 'close-modal'
                },
                { // Specific modal closing rules towards `discipline-detail` or `collection-detail` template
                    from: [
                        `${this.rulesPaths['project-detail']}(.*)`,
                        `${this.rulesPaths['team-detail']}(.*)`,
                        `${this.rulesPaths['project-preview']}(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['discipline-detail']}(.*)`,
                        `${this.rulesPaths['collection-detail']}(.*)`,
                    ],
                    containers: ['#modal', '#listing'],
                    name: 'close-modal-and-toolbar'
                },
                { // Specific modal closing rules from `team-detail `towards `project-preview` template
                    from: [
                        `${this.rulesPaths['team-detail']}(.*)`,
                    ],
                    to: [
                        `${this.rulesPaths['project-showcase']}(.*)`
                        // DO NOT INCLUDE `project-preview` here otherwise it'll conflict w/ redirection system. That's why this rule is separated from the one above
                    ],
                    containers: ['#modal', '#listing'],
                    name: 'close-modal-and-toolbar'
                },
                { // Global modal update rules
                    from: [
                        `${this.rulesPaths['project-detail']}(.*)`,
                        `${this.rulesPaths['team-detail']}(.*)`,
                        `${this.rulesPaths['project-preview']}(.*)`,
                        `${this.rulesPaths['office-detail']}(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['project-detail']}(.*)`,
                        `${this.rulesPaths['team-detail']}(.*)`,
                        `${this.rulesPaths['project-preview']}(.*)`,
                        `${this.rulesPaths['office-detail']}(.*)`,
                    ],
                    containers: ['#modal'],
                    name: 'update-modal'
                },
                { // Global modal closing rules
                    from: [
                        `${this.rulesPaths['project-detail']}(.*)`,
                        `${this.rulesPaths['team-detail']}(.*)`,
                        `${this.rulesPaths['project-preview']}(.*)`,
                        `${this.rulesPaths['article-detail']}(.*)`,
                        `${this.rulesPaths['insights']}(.*)`,
                        `${this.rulesPaths['office-detail']}(.*)`,
                    ],
                    to: [
                        `${this.rulesPaths['projects-listing']}(.*)`,
                        `${this.rulesPaths['team-listing']}(.*)`,
                        `${this.rulesPaths['leadership-listing']}(.*)`,
                        `${this.rulesPaths['founders-listing']}(.*)`,
                        `${this.rulesPaths['search']}(.*)`,
                    ],
                    containers: ['#modal', '#listing', '#toolbar'],
                    name: 'close-modal'
                },
                // ==========================================================================
                // EXPLORE RULES
                // ==========================================================================
                { // Specific modal opening rules from `explore` template
                    from: [
                        `${this.rulesPaths['explore']}(.*)`
                    ],
                    to: [
                        `${this.rulesPaths['project-preview']}(.*)`,
                        `${this.rulesPaths['office-detail']}(.*)`,
                        `${this.rulesPaths['article-detail']}(.*)`,
                        `${this.rulesPaths['insights']}(.*)`,
                    ],
                    containers: ['#modal'],
                    name: 'open-explore-modal'
                },
                { // Specific modal closing rules towards `explore` template
                    from: [
                        `${this.rulesPaths['project-preview']}(.*)`,
                        `${this.rulesPaths['office-detail']}(.*)`,
                        `${this.rulesPaths['article-detail']}(.*)`,
                        `${this.rulesPaths['team-detail']}(.*)`,
                        `${this.rulesPaths['project-detail']}(.*)`,
                        `${this.rulesPaths['insights']}(.*)`,
                    ],
                    to: [
                        `${this.rulesPaths['explore']}(.*)`
                    ],
                    containers: ['#modal', '#listing'],
                    name: 'close-explore-modal'
                }
            ]
        }));
    }

    // ==========================================================================
    // HOOKS
    // ==========================================================================

    async onVisitStart(visit) {
        return new Promise(async resolve => {
            // FRAGMENT VISIT
            if(visit.fragmentVisit) {
                $html.classList.add(CSS_CLASS.LOADING_FRAGMENT)

                // Close all AbstractModal
                for (const modalName of window.modals) {
                    this.call('close', null, modalName)
                }

                switch (visit.fragmentVisit.name) {
                    case 'update-listing':
                        // Scroll to listing according to toolbar
                        const $toolbar = document.querySelector('[data-module-list-toolbar]');
                        const toolbarBCR = $toolbar.getBoundingClientRect();
                        const toolbarMarginBottom = parseFloat(getComputedStyle($toolbar.children[0]).getPropertyValue('margin-bottom'))
                        this.call('scrollTo', { target: '#listing', offset: - toolbarBCR.height - toolbarMarginBottom }, 'Scroll')

                        // Disable clicked sorter
                        if(
                            visit.trigger.el && visit.trigger.el.dataset.load == 'sort-clear' // On sort clear
                        ) {
                            const $sorter = visit.trigger.el.closest('.c-sorter');

                            for(let toRemove of ['-active', '-asc', 'desc'])
                                $sorter.classList.remove(toRemove)

                            await awaitTimeout(150);
                        }

                        // Disable other sorters than the one clicked
                        if(
                            visit.trigger.el && visit.trigger.el.dataset.load == 'sort' // On sorter click
                        ) {
                            // Disable all sorters except the clicked one
                            for(let $sorter of Array.from(this.$('sort'))) {
                                if($sorter != visit.trigger.el)
                                    $sorter.closest('.c-sorter').classList.remove('-active')
                            }
                        }

                        // Hide filters
                        if(
                            !visit.trigger.el // On filters update
                            || visit.trigger.el.tagName == 'FORM' // On search request
                        ) {
                            // Disable all sorters
                            for(let $sorter of Array.from(this.$('sort'))) {
                                $sorter.closest('.c-sorter').classList.remove('-active')
                            }

                            await awaitTimeout(150);
                        }
                        break
                    case 'open-modal':
                        this.call('show', null, 'Dialog')
                        break
                    case 'open-explore-modal':
                        this.call('start', true, 'Explore') // This allows to make sure we've been past the splash screen

                        // Prevent any interactions going forward
                        this.call('preventInteractions', true, 'Explore')
                        await awaitFrame(); // Required for interaction prevent to work

                        // Try and get the ID of the requested modal object
                        this.exploreLocationId = false;
                        let el = visit.trigger.el
                        if(el) {
                            let delay;

                            // Retrieve the ID according to the trigger type
                            if(el.classList.contains('c-card-default_link')) {
                                this.exploreLocationId = el.closest('.c-explore_card')?.dataset?.exploreId;
                                delay = ENV.PREFERS_REDUCED_MOTION ? 16 : 250; // Less delay if it's a card, as the hover will already have triggered a `goToCoordinates`, so the pin is probably close to target
                            } else if(el.classList.contains('c-explore_pin')) {
                                this.exploreLocationId = el.dataset.id;
                                delay = ENV.PREFERS_REDUCED_MOTION ? 16 : 1000; // More delay if it's a pin, as it doesn't move at all on hover so the `goToCoordinates` must have time to get close to target
                            }

                            // If we found an ID, highlight that one exclusively and disable all explore interactions
                            if(this.exploreLocationId) {
                                this.call('focusLocation', {id: this.exploreLocationId, exclusive: true }, 'Explore')
                                await awaitTimeout(delay);
                            }
                        }

                        this.call('show', null, 'Dialog') // open the dialog
                        this.call('setCameraMode', CAMERA_MODE.MODAL, 'ExploreScene') // update the scene camera
                        break;
                    case 'update-modal':

                        let $modal = document.getElementById('modal');
                        // Scroll to top if not in a dialog
                        if(!$modal.closest('.c-dialog')) {
                            this.call('scrollTo', { target: 'top', options: { duration: .1 } }, 'Scroll')
                            await awaitTimeout(250);
                        }

                        break;
                    case 'close-modal':
                    case 'close-modal-and-toolbar':
                        this.call('close', null, 'Dialog')
                        break;
                    case 'close-explore-modal':
                        this.call('destroy', null, 'ExploreFocuser') // Stop focusing on one location
                        this.call('blurLocation', null, 'Explore') // Stop focusing on one location
                        this.call('close', null, 'Dialog') // Close the modal
                        this.call('preventInteractions', false, 'Explore') // Reenable interactions
                        this.call('setCameraMode', CAMERA_MODE.PANEL, 'ExploreScene') // Revert to PANEL camera
                        break;
                }

                resolve();
            } else { // GLOBAL VISIT

                // Close all AbstractModal & Dialog instances
                for (const modalName of window.modals) {
                    this.call('close', null, modalName)
                }
                this.call('close', null, 'Dialog')

                // If going from showcase preview to showcase single, replace the history to prevent reaching preview fullscreen on back history
                if(visit.from.route == 'project-preview' && visit.to.route == 'project-showcase') {
                    visit.history.action = 'replace';
                }

                // When leaving inview dark hero, add specific classes
                $html.classList.remove('coming-from-dark-hero') // Remove it by default
                if($html.dataset.hasMediaHero == 'true' && $html.dataset.hasPlainMediaHero == 'false') { // Make sure we're in a dark hero
                    if($html.classList.contains('has-media-hero-inview') || !$html.classList.contains('has-scrolled')) { // Make sure it's inview or top of screen
                        $html.classList.add('coming-from-dark-hero') // Add specific class, this will removed on next page change only (line above)
                        $html.classList.add('leaving-dark-hero') // Add leaving specific class, this will be removed before content change
                    }

                    // Wait a frame for the next condition to access the class
                    requestAnimationFrame(() => {
                        $html.classList.remove('has-quicknav-visible')
                    })
                }

                // Specific case if quicknav is in viewport but we're not at the top yet
                if(visit.to.route != 'explore' && $html.classList.contains('has-quicknav-visible') && $html.scrollTop != 0) {
                    // Scroll to top
                    this.call('scrollTo', { target: 0, duration: .25, force: true }, 'Scroll')
                    // We're using a timeout instead of scrollTo's `onComplete` to make sure it doesn't get cancelled by a user scroll
                    setTimeout(() => {
                        this.call('stop', null, 'Scroll'); // Kill LS
                        resolve()
                    }, 250);
                } else {
                    this.call('stop', null, 'Scroll'); // Kill LS
                    resolve();
                }
            }
        });
    }

    async beforeContentReplace (visit) {
        // Remove dark hero specific class
        $html.classList.remove('leaving-dark-hero')
        // Remove media hero inview
        $html.classList.remove('has-media-hero-inview')

        // REPLACING CLASS
        $html.classList.add(CSS_CLASS.REPLACING);
        setTimeout(() => {
            $html.classList.remove(CSS_CLASS.REPLACING);
        }, 50)

        if(!visit.fragmentVisit) {
            $html.classList.remove(CSS_CLASS.FIRST_LOAD);
            $html.scrollTop = 0;

            // PAGE REPLACING CLASS
            $html.classList.add(CSS_CLASS.REPLACING_PAGE);
            setTimeout(() => {
                $html.classList.remove(CSS_CLASS.REPLACING_PAGE);
            }, 50)
        } else {
            // FRAGMENT REPLACING CLASS
            $html.classList.add(CSS_CLASS.REPLACING_FRAGMENT);
            setTimeout(() => {
                $html.classList.remove(CSS_CLASS.REPLACING_FRAGMENT);
            }, 50)
        }

        // Cursor reset
        window.dispatchEvent(new CustomEvent(CUSTOM_EVENT.CURSOR_RESET))
    }

    async onContentReplace(visit) {
        const adminBar = document.getElementById('wpadminbar')
        if(adminBar) {
            adminBar.remove();
            var parser = new DOMParser();
            var doc = parser.parseFromString(visit.to.html, "text/html");
            var newAdminBar = doc.getElementById('wpadminbar');
            $body.appendChild(newAdminBar)
        }

        if(visit.fragmentVisit) {
            $html.classList.remove(CSS_CLASS.LOADING_FRAGMENT)

            // Close all AbstractModal
            for (const modalName of window.modals) {
                this.call('rebindTogglers', null, modalName)
            }

            let $modal;
            switch (visit.fragmentVisit.name) {
                case 'update-listing':
                    // Reveal any active sorting button(s)
                    if(
                        !visit.trigger.el // Filters update
                    ) {
                        for(let $sorter of Array.from(this.$('sort'))) {
                            $sorter = $sorter.closest('.c-sorter');
                            if($sorter.classList.contains('-active'))
                                $sorter.classList.add('-reveal')
                        }
                    }

                    // Reveal the clicked sorting button
                    if(
                        visit.trigger.el && visit.trigger.el.dataset.load == 'sort' // Clicked sorting button
                    ) {
                        const $sorter = visit.trigger.el.closest('.c-sorter');
                        const slug = $sorter.dataset.loadSlug;
                        const $newSorter = document.querySelector(`[data-load-slug="${slug}"]`)
                        $newSorter.children?.[0]?.focus();

                        if(!$sorter.classList.contains('-active')) {
                            $newSorter.classList.add('-reveal')
                        }
                    }
                    break;
                case 'close-modal':
                case 'close-modal-and-toolbar':
                case 'close-explore-modal':
                    // If the listing needs updating, it means the page in the background changes, therefore scroll to the top
                    if(visit.containers.includes('#listing')) {
                        $html.scrollTop = 0;
                    }

                    // HACK if we are closing a modal towards an unwanted destination
                    if(
                        // Modal opened from contact trying to close to another template
                        (visit.to.route != 'contact' && $html.dataset.template == "archive-app_office.php") // CONTACT

                        // Modal opened from explore trying to close to another template
                        || (visit.to.route != 'explore' && $html.dataset.template == "template-explore.php")

                        // Modal opened from a listing trying to close towards a discipline or collection template
                        || (visit.fragmentVisit.name == 'close-modal-and-toolbar' && document.querySelectorAll('#toolbar,#filters').length)
                    ) {
                        // If that happens, we DON'T want to only update the `#listing` fragment. We actually want to change the whole page
                        $html.scrollTop = 0;
                        document.querySelector('#listing').innerHTML = ''; // So we strip `#listing` of any content to prevent it showing up
                        this.goTo(visit.to.url) // And we immediately force a new navigation to the real page
                    }
                    break;
                case 'open-modal':
                    // Focus on the #modal element which has an aria-label for screenreader users
                    $modal = document.getElementById('modal');
                    $modal.focus();
                    break;
                case 'update-modal':
                    $modal = document.getElementById('modal');
                    if($modal.closest('.c-dialog')) {
                        this.call('update', null, 'Dialog') // This will scroll to top of modal and focus #modal which has an aria-label for screen-reader users
                    } else { // Redirect if not in modal & a redirect url is defined on #modal
                        if($modal.dataset.redirectUrl) {
                            this.load.navigate($modal.dataset.redirectUrl, { history: 'replace' });
                        }
                    }
                    break
                case 'open-explore-modal':
                    // If we have an ID, it means we only want a slight correction over a long duration (5 secs) as it's already somewhat centered
                    // otherwise, it means we are way off and need to focus quickly so defaults to 1
                    this.call('start', ENV.PREFERS_REDUCED_MOTION ? 0 : (this.exploreLocationId ? 5 : 1), 'ExploreFocuser')
                    break
            }
        } else {
            $html.classList.remove('has-scrolled')
        }
    }

    onVisitAbort(visit) {
        $html.classList.remove(CSS_CLASS.LOADING_FRAGMENT)
    }

    goTo(url) {
        this.load.navigate(url)
    }
}
